import React, { useState, useEffect, useMemo } from 'react';
import AdminSatelliteShell from '../components/admin/AdminSatelliteShell';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { BADGE_CATALOG, getBadgeRarityColor, getBadgeRarityLabel } from '../utils/badgeSystem';
import { 
  Award, 
  Users, 
  Search, 
  X, 
  Check, 
  Filter,
  TrendingUp,
  Shield,
  Star,
  Zap,
  Gift
} from 'lucide-react';

const AdminBadges = () => {
  const { user } = useAuth();
  const { 
    users, 
    badges: allBadges, 
    fetchBadges, 
    awardBadge, 
    revokeBadge 
  } = useData();

  const [activeTab, setActiveTab] = useState('catalog');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRarity, setSelectedRarity] = useState('all');
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [awardReason, setAwardReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBadges();
    }
  }, [user]);

  // Calculate badge statistics
  const badgeStats = useMemo(() => {
    const totalBadges = BADGE_CATALOG.length;
    const totalAwarded = allBadges.length;
    const uniqueRecipients = new Set(allBadges.map(b => b.userId)).size;
    
    // Count by category
    const byCategory = BADGE_CATALOG.reduce((acc, badge) => {
      acc[badge.category] = (acc[badge.category] || 0) + 1;
      return acc;
    }, {});

    // Count awarded by badge type
    const awardedCount = allBadges.reduce((acc, badge) => {
      acc[badge.badgeId] = (acc[badge.badgeId] || 0) + 1;
      return acc;
    }, {});

    return {
      totalBadges,
      totalAwarded,
      uniqueRecipients,
      byCategory,
      awardedCount
    };
  }, [allBadges]);

  // Filter badges
  const filteredBadges = useMemo(() => {
    return BADGE_CATALOG.filter(badge => {
      const matchesSearch = badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          badge.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || badge.category === selectedCategory;
      const matchesRarity = selectedRarity === 'all' || badge.rarity === selectedRarity;
      return matchesSearch && matchesCategory && matchesRarity;
    });
  }, [searchTerm, selectedCategory, selectedRarity]);

  // Get user badges
  const userBadgesMap = useMemo(() => {
    const map = {};
    allBadges.forEach(badge => {
      if (!map[badge.userId]) {
        map[badge.userId] = [];
      }
      map[badge.userId].push(badge);
    });
    return map;
  }, [allBadges]);

  // Filter users
  const filteredUsers = useMemo(() => {
    return (users || []).filter(u => 
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const handleAwardBadge = async () => {
    if (!selectedBadge || !selectedUser) return;
    
    setLoading(true);
    try {
      await awardBadge(selectedUser.id, selectedBadge.id, awardReason || 'Manually awarded by admin');
      alert(`Badge "${selectedBadge.name}" awarded to ${selectedUser.displayName || selectedUser.email}!`);
      setShowAwardModal(false);
      setSelectedBadge(null);
      setSelectedUser(null);
      setAwardReason('');
    } catch (error) {
      console.error('Error awarding badge:', error);
      alert('Failed to award badge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeBadge = async (badgeRecord) => {
    if (!window.confirm(`Are you sure you want to revoke this badge from this user?`)) {
      return;
    }

    setLoading(true);
    try {
      await revokeBadge(badgeRecord.id);
      alert('Badge revoked successfully!');
    } catch (error) {
      console.error('Error revoking badge:', error);
      alert('Failed to revoke badge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      milestone: TrendingUp,
      quality: Star,
      reliability: Shield,
      special: Zap,
      verification: Check
    };
    return icons[category] || Gift;
  };

  return (
    <AdminSatelliteShell
      title="Badge management"
      subtitle="Manage and award badges to recognize outstanding caregivers"
    >
      <div>
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="pf-organic-card pf-organic-card--dark p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Total Badges</div>
                <div className="text-3xl font-bold mt-1">{badgeStats.totalBadges}</div>
              </div>
              <Award size={48} className="opacity-20" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Times Awarded</div>
                <div className="text-3xl font-bold mt-1">{badgeStats.totalAwarded}</div>
              </div>
              <Gift size={48} className="opacity-20" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Badge Holders</div>
                <div className="text-3xl font-bold mt-1">{badgeStats.uniqueRecipients}</div>
              </div>
              <Users size={48} className="opacity-20" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Categories</div>
                <div className="text-3xl font-bold mt-1">{Object.keys(badgeStats.byCategory).length}</div>
              </div>
              <Filter size={48} className="opacity-20" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('catalog')}
                className={`px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === 'catalog'
                    ? 'text-[var(--pf-accent)] border-[var(--pf-accent)]'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                Badge Catalog
              </button>
              <button
                onClick={() => setActiveTab('awarded')}
                className={`px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === 'awarded'
                    ? 'text-[var(--pf-accent)] border-[var(--pf-accent)]'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                Awarded Badges
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === 'users'
                    ? 'text-[var(--pf-accent)] border-[var(--pf-accent)]'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                User Badges
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Search and Filters */}
            <div className="mb-6 flex flex-wrap gap-4">
              <div className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder={activeTab === 'users' ? 'Search users...' : 'Search badges...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  />
                </div>
              </div>

              {activeTab === 'catalog' && (
                <>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="milestone">Milestone</option>
                    <option value="quality">Quality</option>
                    <option value="reliability">Reliability</option>
                    <option value="special">Special</option>
                    <option value="verification">Verification</option>
                  </select>

                  <select
                    value={selectedRarity}
                    onChange={(e) => setSelectedRarity(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  >
                    <option value="all">All Rarities</option>
                    <option value="common">Common</option>
                    <option value="uncommon">Uncommon</option>
                    <option value="rare">Rare</option>
                    <option value="epic">Epic</option>
                    <option value="legendary">Legendary</option>
                  </select>
                </>
              )}
            </div>

            {/* Badge Catalog Tab */}
            {activeTab === 'catalog' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBadges.map((badge) => {
                  const CategoryIcon = getCategoryIcon(badge.category);
                  const awardedCount = badgeStats.awardedCount[badge.id] || 0;

                  return (
                    <div
                      key={badge.id}
                      className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow bg-white"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`text-5xl bg-gradient-to-br ${badge.color} bg-clip-text`}>
                          {badge.icon}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${getBadgeRarityColor(badge.rarity)} bg-opacity-10`}>
                            {getBadgeRarityLabel(badge.rarity)}
                          </span>
                          <CategoryIcon size={16} className="text-gray-400" />
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 mb-2">{badge.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{badge.description}</p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="text-sm text-gray-500">
                          Awarded: <span className="font-semibold text-gray-900">{awardedCount}</span> times
                        </div>
                        <button
                          onClick={() => {
                            setSelectedBadge(badge);
                            setShowAwardModal(true);
                          }}
                          className="pf-btn-primary px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          Award
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Awarded Badges Tab */}
            {activeTab === 'awarded' && (
              <div className="space-y-4">
                {allBadges.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Award size={64} className="mx-auto text-gray-300 mb-4" />
                    <p>No badges have been awarded yet</p>
                  </div>
                ) : (
                  allBadges.map((badgeRecord) => {
                    const badge = BADGE_CATALOG.find(b => b.id === badgeRecord.badgeId);
                    const user = users.find(u => u.id === badgeRecord.userId);
                    
                    if (!badge) return null;

                    return (
                      <div
                        key={badgeRecord.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-3xl">{badge.icon}</div>
                          <div>
                            <div className="font-semibold text-gray-900">{badge.name}</div>
                            <div className="text-sm text-gray-600">
                              Awarded to: <span className="font-medium">{user?.displayName || user?.email || 'Unknown User'}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(badgeRecord.earnedAt?.toDate?.() || badgeRecord.earnedAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeBadge(badgeRecord)}
                          disabled={loading}
                          className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                        >
                          Revoke
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* User Badges Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {filteredUsers.map((usr) => {
                  const userBadges = userBadgesMap[usr.id] || [];
                  
                  return (
                    <div
                      key={usr.id}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="font-semibold text-gray-900">{usr.displayName || 'No Name'}</div>
                          <div className="text-sm text-gray-600">{usr.email}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            Role: <span className="capitalize font-medium">{usr.role}</span>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-[var(--pf-accent)]">{userBadges.length}</div>
                          <div className="text-xs text-gray-500">Badges</div>
                        </div>
                      </div>

                      {userBadges.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {userBadges.map((badgeRecord) => {
                            const badge = BADGE_CATALOG.find(b => b.id === badgeRecord.badgeId);
                            if (!badge) return null;

                            return (
                              <div
                                key={badgeRecord.id}
                                className={`flex items-center gap-2 px-3 py-2 bg-gradient-to-r ${badge.color} rounded-lg text-white text-sm`}
                                title={badge.description}
                              >
                                <span>{badge.icon}</span>
                                <span className="font-medium">{badge.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-400 text-sm">
                          No badges earned yet
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Award Badge Modal */}
      {showAwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Award Badge</h3>
              <button
                onClick={() => {
                  setShowAwardModal(false);
                  setSelectedBadge(null);
                  setSelectedUser(null);
                  setAwardReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {selectedBadge && (
              <div className={`flex items-center gap-3 p-4 bg-gradient-to-r ${selectedBadge.color} bg-opacity-10 rounded-lg mb-4`}>
                <div className="text-3xl">{selectedBadge.icon}</div>
                <div>
                  <div className="font-semibold text-gray-900">{selectedBadge.name}</div>
                  <div className="text-sm text-gray-600">{selectedBadge.description}</div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User
                </label>
                <select
                  value={selectedUser?.id || ''}
                  onChange={(e) => {
                    const usr = users.find(u => u.id === e.target.value);
                    setSelectedUser(usr);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                >
                  <option value="">Choose a user...</option>
                  {users.map((usr) => (
                    <option key={usr.id} value={usr.id}>
                      {usr.displayName || usr.email} ({usr.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={awardReason}
                  onChange={(e) => setAwardReason(e.target.value)}
                  placeholder="Why are you awarding this badge?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  rows="3"
                />
              </div>

              <button
                onClick={handleAwardBadge}
                disabled={!selectedUser || loading}
                className="pf-btn-primary w-full py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Awarding...' : 'Award Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminSatelliteShell>
  );
};

export default AdminBadges;

